/**
 * LeaderboardPage – Shows top 10 user scores for each track
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TrackSummary } from '../types';
import { useAuth } from '../context/AuthContext';
import { useInView, usePageLoadAnimation } from '../hooks/useAnimation';
import { API_BASE_URL } from '../config';

interface LeaderboardEntry {
  id: number;
  username: string;
  score: number;
  accuracy: number;
  max_combo: number;
  perfect_count: number;
  good_count: number;
  miss_count: number;
  total_notes: number;
  grade: string;
  created_at: string;
}

const GRADE_COLORS: Record<string, string> = {
  S: '#ffd700',  // Gold
  'A+': '#81b29a',
  A: '#81b29a',
  B: '#f5a623',
  C: '#e07a5f',
  D: '#e07a5f',
  F: '#d4a574',
};

const RANK_COLORS: Record<number, string> = {
  1: '#ffd700', // Gold
  2: '#c0c0c0', // Silver
  3: '#cd7f32', // Bronze
};

export const LeaderboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [tracks, setTracks] = useState<TrackSummary[]>([]);
  const [selectedTrack, setSelectedTrack] = useState<TrackSummary | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  
  usePageLoadAnimation();
  const [headerRef, headerInView] = useInView<HTMLDivElement>();
  const [listRef, listInView] = useInView<HTMLDivElement>();
  
  const trackIdFromUrl = searchParams.get('track');

  // Fetch all tracks
  const fetchTracks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/tracks`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as TrackSummary[];
      setTracks(data);
      // Auto-select track from URL param, or first track if available
      if (trackIdFromUrl) {
        const trackFromParam = data.find(t => t.id === trackIdFromUrl);
        if (trackFromParam) {
          setSelectedTrack(trackFromParam);
        } else if (data.length > 0) {
          setSelectedTrack(data[0]);
        }
      } else if (data.length > 0) {
        setSelectedTrack(data[0]);
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchTracks();
  }, [fetchTracks]);

  // Fetch leaderboard when track changes
  useEffect(() => {
    if (!selectedTrack) return;
    
    const fetchLeaderboard = async () => {
      setLeaderboardLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/tracks/${selectedTrack.id}/scores`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as LeaderboardEntry[];
        // Take top 10
        setLeaderboard(data.slice(0, 10));
      } catch (e) {
        console.error('Failed to fetch leaderboard:', e);
        setLeaderboard([]);
      } finally {
        setLeaderboardLoading(false);
      }
    };
    
    void fetchLeaderboard();
  }, [selectedTrack]);

  const handlePlayTrack = (trackId: string) => {
    if (user) {
      navigate(`/game/${trackId}`);
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen bg-studio-950">
      {/* Header Section */}
      <section className="relative pt-24 pb-12 px-4 sm:px-8 lg:px-16">
        <div className="absolute inset-0 bg-studio-950">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(245,166,35,0.08)_0%,transparent_50%)]" />
        </div>
        
        <div 
          ref={headerRef}
          className="max-w-7xl mx-auto relative"
        >
          <div className={`text-center mb-12 transition-all duration-700 ${headerInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="text-amber-500 text-sm font-semibold tracking-widest uppercase mb-4 block">
              Global Rankings
            </span>
            <h1 className="font-display text-5xl lg:text-6xl font-bold text-stone-100 mb-4">
              Leader<span className="italic text-amber-500">board</span>
            </h1>
            <p className="text-stone-400 text-lg max-w-2xl mx-auto">
              Compete with rhythm masters worldwide. See who&apos;s hitting the highest scores on each track.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="px-4 sm:px-8 lg:px-16 pb-24">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-24">
              <div className="flex flex-col items-center gap-4">
                <div className="w-12 h-12 border-3 border-amber-500 border-t-transparent rounded-full animate-spin" />
                <p className="text-stone-500">Loading tracks...</p>
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-24 animate-fade-in">
              <p className="text-coral-400 text-lg mb-4">{error}</p>
              <button type="button" onClick={() => void fetchTracks()} className="btn-secondary">
                Try Again
              </button>
            </div>
          ) : tracks.length === 0 ? (
            <div className="text-center py-24 border-2 border-dashed border-studio-800 rounded-3xl">
              <p className="text-stone-500 text-lg mb-4">No tracks available yet.</p>
              {user?.is_admin && (
                <button type="button" onClick={() => navigate('/admin')} className="btn-primary">
                  Upload Track
                </button>
              )}
            </div>
          ) : (
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Track Selection Sidebar */}
              <div className="lg:col-span-1">
                <div className="bg-studio-900/50 border border-studio-800 rounded-2xl p-6 sticky top-24">
                  <h2 className="text-stone-100 font-semibold text-lg mb-4 flex items-center gap-2">
                    <svg className="w-5 h-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" />
                    </svg>
                    Select Track
                  </h2>
                  <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
                    {tracks.map((track, index) => (
                      <TrackListItem
                        key={track.id}
                        track={track}
                        isSelected={selectedTrack?.id === track.id}
                        onClick={() => setSelectedTrack(track)}
                        index={index}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Leaderboard Display */}
              <div 
                ref={listRef}
                className="lg:col-span-2"
              >
                {selectedTrack && (
                  <div className={`transition-all duration-700 ${listInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                    {/* Track Info Header */}
                    <div className="bg-gradient-to-r from-studio-900/80 to-studio-900/40 border border-studio-800 rounded-2xl p-6 mb-6">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                          <h2 className="text-2xl font-bold text-stone-100 mb-1">{selectedTrack.title}</h2>
                          <p className="text-stone-500">{selectedTrack.artist ?? 'Unknown Artist'}</p>
                          <div className="flex items-center gap-4 mt-3">
                            {selectedTrack.bpm && (
                              <span className="text-stone-400 text-sm font-mono">
                                {Math.round(selectedTrack.bpm)} BPM
                              </span>
                            )}
                            <span className={`text-xs font-bold px-2 py-1 rounded ${getDifficultyStyle(selectedTrack.difficulty)}`}>
                              {(selectedTrack.difficulty ?? 'medium').toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handlePlayTrack(selectedTrack.id)}
                          className="btn-primary whitespace-nowrap"
                        >
                          {user ? 'Play Track →' : 'Sign In to Play'}
                        </button>
                      </div>
                    </div>

                    {/* Leaderboard Table */}
                    <div className="bg-studio-900/30 border border-studio-800 rounded-2xl overflow-hidden">
                      {leaderboardLoading ? (
                        <div className="flex justify-center py-24">
                          <div className="w-10 h-10 border-3 border-amber-500 border-t-transparent rounded-full animate-spin" />
                        </div>
                      ) : leaderboard.length === 0 ? (
                        <div className="text-center py-16">
                          <p className="text-stone-500 text-lg mb-2">No scores yet</p>
                          <p className="text-stone-600 text-sm">Be the first to set a record!</p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-studio-800 bg-studio-900/50">
                                <th className="text-left py-4 px-4 sm:px-6 text-stone-500 text-xs font-semibold uppercase tracking-wider w-16">Rank</th>
                                <th className="text-left py-4 px-4 sm:px-6 text-stone-500 text-xs font-semibold uppercase tracking-wider">Player</th>
                                <th className="text-right py-4 px-4 sm:px-6 text-stone-500 text-xs font-semibold uppercase tracking-wider">Score</th>
                                <th className="text-center py-4 px-4 sm:px-6 text-stone-500 text-xs font-semibold uppercase tracking-wider hidden sm:table-cell">Grade</th>
                                <th className="text-right py-4 px-4 sm:px-6 text-stone-500 text-xs font-semibold uppercase tracking-wider hidden md:table-cell">Accuracy</th>
                                <th className="text-right py-4 px-4 sm:px-6 text-stone-500 text-xs font-semibold uppercase tracking-wider hidden lg:table-cell">Max Combo</th>
                              </tr>
                            </thead>
                            <tbody>
                              {leaderboard.map((entry, index) => (
                                <LeaderboardRow
                                  key={entry.id}
                                  entry={entry}
                                  rank={index + 1}
                                  isCurrentUser={entry.username === user?.username}
                                  animationDelay={index * 50}
                                />
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    {/* Stats Summary */}
                    {leaderboard.length > 0 && (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
                        <StatCard
                          label="Total Players"
                          value={leaderboard.length}
                          icon="👥"
                        />
                        <StatCard
                          label="Highest Score"
                          value={leaderboard[0]?.score.toLocaleString() ?? '-'}
                          icon="🏆"
                        />
                        <StatCard
                          label="Top Accuracy"
                          value={`${leaderboard[0]?.accuracy.toFixed(1) ?? '-'}%`}
                          icon="🎯"
                        />
                        <StatCard
                          label="Best Combo"
                          value={leaderboard[0]?.max_combo.toLocaleString() ?? '-'}
                          icon="⚡"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

// ============================================
// Sub-components
// ============================================

interface TrackListItemProps {
  track: TrackSummary;
  isSelected: boolean;
  onClick: () => void;
  index: number;
}

function TrackListItem({ track, isSelected, onClick, index }: TrackListItemProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left p-3 rounded-xl transition-all duration-200 group ${
        isSelected
          ? 'bg-amber-500/20 border border-amber-500/50'
          : 'bg-studio-950/50 border border-transparent hover:bg-studio-800/50 hover:border-studio-700'
      }`}
      style={{
        animationDelay: `${index * 30}ms`,
      }}
    >
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${
          isSelected ? 'bg-amber-500 text-studio-950' : 'bg-studio-800 text-stone-400'
        }`}>
          {index + 1}
        </div>
        <div className="flex-1 min-w-0">
          <p className={`font-semibold truncate ${isSelected ? 'text-amber-500' : 'text-stone-100 group-hover:text-stone-200'}`}>
            {track.title}
          </p>
          <p className="text-stone-500 text-xs truncate">{track.artist ?? 'Unknown Artist'}</p>
        </div>
        {isSelected && (
          <svg className="w-5 h-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        )}
      </div>
    </button>
  );
}

interface LeaderboardRowProps {
  entry: LeaderboardEntry;
  rank: number;
  isCurrentUser: boolean;
  animationDelay: number;
}

function LeaderboardRow({ entry, rank, isCurrentUser, animationDelay }: LeaderboardRowProps) {
  const rankColor = RANK_COLORS[rank] ?? '#78716c';
  const gradeColor = GRADE_COLORS[entry.grade] ?? '#78716c';
  
  return (
    <tr 
      className={`border-b border-studio-800/50 transition-all duration-300 hover:bg-studio-800/30 ${
        isCurrentUser ? 'bg-amber-500/5' : ''
      }`}
      style={{
        animation: `fadeSlideIn 0.5s ease-out ${animationDelay}ms both`,
      }}
    >
      {/* Rank */}
      <td className="py-4 px-4 sm:px-6">
        <div 
          className="w-10 h-10 rounded-xl flex items-center justify-center font-display text-lg font-bold"
          style={{ 
            backgroundColor: rank <= 3 ? `${rankColor}20` : 'transparent',
            color: rankColor,
            border: rank <= 3 ? `2px solid ${rankColor}40` : '2px solid transparent',
          }}
        >
          {rank}
        </div>
      </td>
      
      {/* Player */}
      <td className="py-4 px-4 sm:px-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-500/20 to-coral-400/20 flex items-center justify-center text-sm font-bold text-amber-500">
            {entry.username.charAt(0).toUpperCase()}
          </div>
          <div>
            <p className={`font-semibold ${isCurrentUser ? 'text-amber-500' : 'text-stone-100'}`}>
              {entry.username}
              {isCurrentUser && <span className="ml-2 text-xs text-amber-500/70">(You)</span>}
            </p>
            <p className="text-stone-600 text-xs">{formatDateShort(entry.created_at)}</p>
          </div>
        </div>
      </td>
      
      {/* Score */}
      <td className="py-4 px-4 sm:px-6 text-right">
        <span className="font-display text-xl font-bold text-stone-100">
          {entry.score.toLocaleString()}
        </span>
      </td>
      
      {/* Grade */}
      <td className="py-4 px-4 sm:px-6 text-center hidden sm:table-cell">
        <span 
          className="inline-flex items-center justify-center w-10 h-10 rounded-lg font-display text-lg font-bold"
          style={{ 
            backgroundColor: `${gradeColor}20`,
            color: gradeColor,
            border: `2px solid ${gradeColor}40`,
          }}
        >
          {entry.grade}
        </span>
      </td>
      
      {/* Accuracy */}
      <td className="py-4 px-4 sm:px-6 text-right hidden md:table-cell">
        <div className="flex items-center justify-end gap-2">
          <div className="w-16 h-1.5 bg-studio-800 rounded-full overflow-hidden">
            <div 
              className="h-full rounded-full"
              style={{ 
                width: `${entry.accuracy}%`,
                backgroundColor: entry.accuracy >= 90 ? '#81b29a' : entry.accuracy >= 70 ? '#f5a623' : '#e07a5f'
              }}
            />
          </div>
          <span className="font-mono text-sm text-stone-300 w-14">
            {entry.accuracy.toFixed(1)}%
          </span>
        </div>
      </td>
      
      {/* Max Combo */}
      <td className="py-4 px-4 sm:px-6 text-right hidden lg:table-cell">
        <span className="font-mono text-stone-300">
          {entry.max_combo.toLocaleString()}
          <span className="text-stone-600 text-xs ml-1">x</span>
        </span>
      </td>
    </tr>
  );
}

interface StatCardProps {
  label: string;
  value: string | number;
  icon: string;
}

function StatCard({ label, value, icon }: StatCardProps) {
  return (
    <div className="bg-studio-900/30 border border-studio-800 rounded-xl p-4 text-center">
      <span className="text-2xl mb-2 block">{icon}</span>
      <p className="font-display text-xl font-bold text-stone-100">{value}</p>
      <p className="text-stone-500 text-xs">{label}</p>
    </div>
  );
}

function getDifficultyStyle(difficulty: string | undefined): string {
  const styles: Record<string, string> = {
    easy: 'bg-sage-400/20 text-sage-400',
    medium: 'bg-amber-500/20 text-amber-500',
    hard: 'bg-coral-400/20 text-coral-400',
    expert: 'bg-purple-400/20 text-purple-400',
  };
  return styles[difficulty ?? 'medium'] ?? styles.medium;
}

function formatDateShort(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Add keyframe animation
const style = document.createElement('style');
style.textContent = `
  @keyframes fadeSlideIn {
    from {
      opacity: 0;
      transform: translateX(-10px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-track {
    background: rgba(31, 31, 32, 0.5);
    border-radius: 3px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(245, 166, 35, 0.3);
    border-radius: 3px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: rgba(245, 166, 35, 0.5);
  }
`;
document.head.appendChild(style);
